import React from "react";
import {Button, Icon, Tooltip, Typography} from "antd";
import {PaxNode} from "../../__generated__/graphql/types";

const XBR_FAIL_PREFIX = '\u2194 ';
const XBR_PASS_PREFIX = '\uc6c3 ';

/**
 * Resolves the extraBroadState for a pax/person record.
 * Checks record.personID.extraBroadState first, then falls back to record.extraBroadState.
 * Also handles the case where the record itself is a Person (record.__typename === 'Person').
 */
export function resolveExtraBroadState(record: any): string | undefined {
    if (!record) return undefined;
    // If this record is a raw Person (not a paxnode), treat the record itself as personID
    const personID = record.__typename === 'Person' ? record : record.personID;
    return (personID && personID.extraBroadState) || record.extraBroadState;
}

/**
 * Formats a passenger's display name, prepending an XBR prefix symbol if applicable.
 */
export function formatPaxName(record: any): string {
    const xbr = resolveExtraBroadState(record);
    const base = `${record.lastName}, ${record.firstName}`;
    if (xbr === 'FAIL') return XBR_FAIL_PREFIX + base;
    if (xbr === 'PASS') return XBR_PASS_PREFIX + base;
    return base;
}

export type PaxNameCellRecord = Pick<PaxNode, '_id'> & Partial<PaxNode>

export interface PaxNameCellProps {
    /** The pax/person record */
    record: any;
    /** If provided, the name is rendered as a clickable link-style button */
    onClick?: (record: any) => void;
    /** Extra className applied to the outer span */
    className?: string;
    /** Extra content rendered before the name (e.g. a flight icon) */
    prefix?: React.ReactNode;
    /**
     * When true, displays a red exclamation icon with a tooltip if record.personID is null/undefined,
     * indicating the associated person record has been deleted. Defaults to false.
     */
    showDeletedPersonNotice?: boolean;

    /**
     * Overrides the tooltip displayed when user hovers a over red exclamation icon if record.personID is null/undefined.
     */
    deletedPersonNoticeTooltip?: string;
}

/**
 * Renders a passenger name cell with XBR flair, an optional prefix node (e.g. on-flight icon),
 * and optionally as a clickable button.
 * If record.personID is null/undefined, renders a red warning icon instead of the name.
 */
const PaxNameCell: React.FC<PaxNameCellProps> = (
    {
        record,
        onClick,
        className,
        prefix,
        showDeletedPersonNotice = false,
        deletedPersonNoticeTooltip = "This passenger's associated person record has been deleted."
}) => {
    const name = formatPaxName(record);

    const deletedNotice = showDeletedPersonNotice && !record.personID ? (
        <Tooltip title={deletedPersonNoticeTooltip}>
            <Typography.Text type="danger">
                <Icon type="exclamation-circle" style={{ marginRight: 6 }} />
            </Typography.Text>
        </Tooltip>
    ) : null;

    return (
        <span className={className}>
            {prefix}
            {deletedNotice}
            {onClick ? (
                <Button
                    className={'mc-link-btn' + (className ? ' ' + className : '')}
                    onClick={() => onClick(record)}
                >
                    {name}
                </Button>
            ) : (
                <span>{name}</span>
            )}
        </span>
    );
};

export default PaxNameCell;

